import React, { useState, useEffect, useRef } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  List,
  ListItem,
  Collapse,
  Paper,
  Toolbar,
  InputAdornment,
  Button
} from '@mui/material';
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Search as SearchIcon,
  Settings as SettingsIcon,
  BugReport as BugReportIcon
} from '@mui/icons-material';
import { useDebugLogger } from '../../hooks/useDebugLogger';
import { useDebugContext } from './DebugProvider';
import { DebugEntry, DebugLevel, DebugModule } from '../../types/debug';

interface DebugPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export const DebugPanel: React.FC<DebugPanelProps> = ({ isOpen, onClose }) => {
  const { currentModule } = useDebugContext();
  const { 
    entries, 
    clearLogs, 
    exportLogs, 
    filterByModule, 
    filterByLevel,
    searchLogs,
    getModuleStats,
    config
  } = useDebugLogger();

  const [selectedLevel, setSelectedLevel] = useState<DebugLevel | 'all'>('all');
  const [selectedModule, setSelectedModule] = useState<DebugModule | 'all'>(currentModule);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedEntries, setExpandedEntries] = useState<Set<string>>(new Set());

  // Update selected module when current module changes (from route or explicit setting)
  useEffect(() => {
    if (currentModule && currentModule !== selectedModule) {
      setSelectedModule(currentModule);
    }
  }, [currentModule]);


  // Filter entries based on current filters
  const filteredEntries = entries.filter(entry => {
    const levelMatch = selectedLevel === 'all' || entry.level === selectedLevel;
    const moduleMatch = selectedModule === 'all' || entry.module === selectedModule;
    const searchMatch = !searchTerm || 
      entry.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entry.component?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      JSON.stringify(entry.data).toLowerCase().includes(searchTerm.toLowerCase());
    
    return levelMatch && moduleMatch && searchMatch;
  });

  const toggleEntryExpansion = (entryId: string) => {
    const newExpanded = new Set(expandedEntries);
    if (newExpanded.has(entryId)) {
      newExpanded.delete(entryId);
    } else {
      newExpanded.add(entryId);
    }
    setExpandedEntries(newExpanded);
  };

  const getLevelColor = (level: DebugLevel) => {
    const colors = {
      error: { primary: '#f44336', secondary: '#ffebee' },    // Red
      warn: { primary: '#ff9800', secondary: '#fff3e0' },     // Orange  
      info: { primary: '#4caf50', secondary: '#e8f5e9' },     // Green
      debug: { primary: '#9e9e9e', secondary: '#f5f5f5' },    // Gray
      trace: { primary: '#9c27b0', secondary: '#f3e5f5' }     // Purple
    };
    return colors[level] || colors.debug;
  };

  const getModuleColor = (module: DebugModule) => {
    const colors = {
      'serp-tool': 'success',
      'dataforseo-tool': 'primary', 
      'ai-analyst': 'secondary',
      'explore': 'info',
      'snowflake': 'info',
      'qlik': 'warning',
      'auth': 'error',
      'api': 'warning',
      'general': 'default'
    };
    return colors[module] || 'default';
  };

  const moduleStats = getModuleStats();

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 600,
          maxWidth: '90vw'
        }
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Toolbar 
          sx={{ 
            borderBottom: 1, 
            borderColor: 'divider',
            backgroundColor: 'grey.50',
            minHeight: '64px !important'
          }}
        >
          <BugReportIcon sx={{ mr: 2, color: 'primary.main' }} />
          <Typography variant="h6" component="h2" sx={{ flexGrow: 1 }}>
            Debug Panel
          </Typography>
          
          {/* Module Stats */}
          <Box sx={{ display: 'flex', gap: 1, mr: 2 }}>
            {Object.entries(moduleStats).slice(0, 3).map(([module, count]) => (
              <Chip
                key={module}
                label={`${module}: ${count}`}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.75rem' }}
              />
            ))}
          </Box>

          {/* Action Buttons */}
          <IconButton 
            onClick={() => exportLogs()} 
            title="Export Logs"
            size="small"
          >
            <DownloadIcon />
          </IconButton>
          <IconButton 
            onClick={clearLogs} 
            title="Clear Logs"
            size="small"
          >
            <DeleteIcon />
          </IconButton>
          <IconButton 
            onClick={onClose} 
            title="Close"
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Toolbar>


        {/* Filters */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', backgroundColor: 'background.paper' }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2, 
            alignItems: { xs: 'stretch', sm: 'center' },
            flexWrap: 'wrap'
          }}>
            {/* Search */}
            <TextField
              placeholder="Search logs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              variant="outlined"
              sx={{ flexGrow: 1, minWidth: { xs: '100%', sm: '200px' } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            {/* Level Filter */}
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: '120px' } }}>
              <InputLabel>Level</InputLabel>
              <Select
                value={selectedLevel}
                onChange={(e) => setSelectedLevel(e.target.value as DebugLevel | 'all')}
                label="Level"
              >
                <MenuItem value="all">All Levels</MenuItem>
                <MenuItem value="error">Error</MenuItem>
                <MenuItem value="warn">Warning</MenuItem>
                <MenuItem value="info">Info</MenuItem>
                <MenuItem value="debug">Debug</MenuItem>
                <MenuItem value="trace">Trace</MenuItem>
              </Select>
            </FormControl>

            {/* Module Filter */}
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: '140px' } }}>
              <InputLabel>Module</InputLabel>
              <Select
                value={selectedModule}
                onChange={(e) => setSelectedModule(e.target.value as DebugModule | 'all')}
                label="Module"
              >
                <MenuItem value="all">All Modules</MenuItem>
                {config.modules.map(module => (
                  <MenuItem key={module} value={module}>
                    {module}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Entry Count */}
            <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center', whiteSpace: 'nowrap' }}>
              {filteredEntries.length} / {entries.length} entries
            </Typography>
          </Box>
        </Box>

        {/* Log Entries */}
        <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
          {filteredEntries.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
              <Typography variant="body2" color="text.secondary">
                {entries.length === 0 ? 'No debug entries yet' : 'No entries match current filters'}
              </Typography>
            </Box>
          ) : (
            <List sx={{ height: '100%', overflow: 'auto', p: 0 }}>
              {filteredEntries.map((entry) => {
                const levelColors = getLevelColor(entry.level);
                const isExpanded = expandedEntries.has(entry.id);
                
                return (
                  <ListItem
                    key={entry.id}
                    sx={{
                      borderLeft: 4,
                      borderLeftColor: levelColors.primary,
                      backgroundColor: levelColors.secondary,
                      mb: 0.5,
                      borderRadius: 1,
                      flexDirection: 'column',
                      alignItems: 'stretch',
                      py: 1
                    }}
                  >
                    {/* Header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', mb: 1 }}>
                      <IconButton
                        onClick={() => toggleEntryExpansion(entry.id)}
                        size="small"
                        sx={{ mr: 1 }}
                      >
                        {isExpanded ? <ExpandMoreIcon /> : <ExpandMoreIcon sx={{ transform: 'rotate(-90deg)' }} />}
                      </IconButton>
                      
                      <Chip
                        label={entry.level.toUpperCase()}
                        size="small"
                        sx={{
                          backgroundColor: levelColors.primary,
                          color: 'white',
                          fontWeight: 'bold',
                          mr: 1,
                          minWidth: 60
                        }}
                      />
                      
                      <Chip
                        label={entry.module}
                        size="small"
                        color={getModuleColor(entry.module) as any}
                        variant="outlined"
                        sx={{ mr: 1 }}
                      />
                      
                      {entry.component && (
                        <Chip
                          label={entry.component}
                          size="small"
                          variant="outlined"
                          sx={{ mr: 1, fontSize: '0.7rem' }}
                        />
                      )}
                      
                      <Box sx={{ flexGrow: 1 }} />
                      
                      <Typography variant="caption" color="text.secondary">
                        {new Date(entry.timestamp).toLocaleTimeString()}
                      </Typography>
                    </Box>
                    
                    {/* Message */}
                    <Typography variant="body2" sx={{ fontWeight: 500, mb: isExpanded ? 1 : 0 }}>
                      {entry.message}
                    </Typography>
                    
                    {/* Expanded Content */}
                    <Collapse in={isExpanded}>
                      <Box sx={{ pt: 1, borderTop: 1, borderColor: 'divider' }}>
                        {entry.data && Object.keys(entry.data).length > 0 && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                              Data:
                            </Typography>
                            <Paper variant="outlined" sx={{ p: 1, backgroundColor: 'grey.100' }}>
                              <Typography
                                component="pre"
                                variant="caption"
                                sx={{ 
                                  fontFamily: 'monospace',
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                  fontSize: '0.7rem'
                                }}
                              >
                                {JSON.stringify(entry.data, null, 2)}
                              </Typography>
                            </Paper>
                          </Box>
                        )}
                        
                        {entry.stack && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                              Stack Trace:
                            </Typography>
                            <Paper variant="outlined" sx={{ p: 1, backgroundColor: '#ffebee', color: '#c62828' }}>
                              <Typography
                                component="pre"
                                variant="caption"
                                sx={{ 
                                  fontFamily: 'monospace',
                                  whiteSpace: 'pre-wrap',
                                  fontSize: '0.7rem'
                                }}
                              >
                                {entry.stack}
                              </Typography>
                            </Paper>
                          </Box>
                        )}
                        
                        <Typography variant="caption" color="text.secondary">
                          ID: {entry.id} | {new Date(entry.timestamp).toISOString()}
                        </Typography>
                      </Box>
                    </Collapse>
                  </ListItem>
                );
              })}
            </List>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};
